import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { usePermissionStore } from '@kolab/dms-base/stores'
import type { Permission } from './permissions'

interface PermissionRouteProps {
  /** Permission(s) required to reach this route. */
  permission: Permission | Permission[]
  /**
   * When true, the user needs ALL listed permissions. Default: any single
   * one is enough. Matches the semantics of the shared `<Can>` component.
   */
  requireAll?: boolean
  /** Redirect target when the user lacks the permission. Defaults to /403. */
  redirectTo?: string
  children: ReactNode
}

/**
 * Route-level permission gate. Wrap a route element to enforce access at the
 * URL layer — a user who lands via typed URL or shared link and lacks the
 * permission is bounced to /403.
 *
 * Menu-level hiding is done separately in menu.ts + Sidebar; this component
 * exists so that hiding a sidebar item is not enough to prevent access, i.e.
 * defense-in-depth. If a page is accessible only via a permission, ALSO wrap
 * its Route in <PermissionRoute>.
 *
 * Backend enforcement is authoritative — the interceptor in nirixis-admin
 * gates the RPCs themselves. This component prevents the UI from making a
 * useless call that will 403 anyway, and gives users a clear "not allowed"
 * signal rather than a broken page.
 */
export function PermissionRoute({
  permission,
  requireAll = false,
  redirectTo = '/403',
  children,
}: PermissionRouteProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoaded } = usePermissionStore()

  // Permissions are hydrated by useAuth on login; on a first paint before /me
  // resolves (or during a full page reload) isLoaded is false. Render nothing
  // rather than bouncing to /403 in that flicker window — the store's own
  // localStorage rehydration handles common cases.
  if (!isLoaded) {
    return null
  }

  const list: Permission[] = Array.isArray(permission) ? permission : [permission]

  const allowed = requireAll
    ? hasAllPermissions(list)
    : list.length === 1
      ? hasPermission(list[0]!)
      : hasAnyPermission(list)

  if (!allowed) {
    return <Navigate to={redirectTo} replace />
  }
  return <>{children}</>
}
